import type { IntlShape } from 'react-intl'

import { isValid, parseISO, startOfDay, subYears } from 'date-fns'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import type {
  CareHistoryEpisode,
  CareHistoryProvider,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

export type PastCareRow = { provider: CareHistoryProvider; episodes: CareHistoryEpisode[] }
export type PastCareGroup = { programName: string | null; providers: PastCareRow[] }

// The API returns all-time history. This surface is limited to the last three years.
// An undated relationship cannot establish that it falls inside that window.
export const groupPastCare = (providers: CareHistoryProvider[], now = new Date()): PastCareGroup[] => {
  const cutoff = startOfDay(subYears(now, 3))
  const groups = new Map<string | null, PastCareGroup>()
  for (const provider of providers) {
    for (const episode of provider.historical_episodes) {
      const lastCareDate = parseISO(episode.end_date ?? episode.start_date ?? '')
      if (!isValid(lastCareDate) || lastCareDate < cutoff) continue
      const programName = episode.program_name?.trim() || null
      let group = groups.get(programName)
      if (!group) {
        group = { programName, providers: [] }
        groups.set(programName, group)
      }
      let row = group.providers.find((item) => item.provider.participant_lyra_id === provider.participant_lyra_id)
      if (!row) {
        row = { provider, episodes: [] }
        group.providers.push(row)
      }
      row.episodes.push(episode)
    }
  }
  return [...groups.values()]
}

export const getPastCareProgramLabel = (intl: IntlShape, programName: string | null): string =>
  getProgramDisplayLabel(intl, programName ?? undefined, 'clientProfile') ||
  programName ||
  intl.formatMessage({ defaultMessage: 'Other care', description: 'Past care group when program metadata is missing' })

export const formatPastCareDates = (intl: IntlShape, episodes: CareHistoryEpisode[]): string => {
  const formatDate = (value: string | null) => {
    const date = parseISO(value ?? '')
    return isValid(date) ? intl.formatDate(date, { month: 'short', year: 'numeric' }) : ''
  }
  const ranges = episodes.map(({ start_date, end_date }) =>
    [...new Set([formatDate(start_date), formatDate(end_date)].filter(Boolean))].join(' – '),
  )
  return [...new Set(ranges)].filter(Boolean).join('; ')
}
